import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Download,
  Ellipsis,
  Lock,
  MapPin,
  PackageSearch,
  Pencil,
  Printer,
  Scale,
  SearchX,
  Trash2,
  TriangleAlert,
  Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterChip } from '@/components/ui/filter-chip';
import { ScanButton } from '@/components/ui/barcode-scanner';
import { notify } from '@/components/ui/notify';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  BulkBar,
  BulkButton,
  Callout,
  ChipSelect,
  ExportMenu,
  InvCheckbox,
  LocationTag,
  ScrollFade,
  SearchField,
  Segmented,
  TableFoot,
  TableScroll,
  TableWrap,
  useMediaQuery,
  numClass,
  subClass,
  tableClass,
  tdClass,
  thClass
} from './InventoryUi';
import {
  EMPTY_FILTER_VALUE,
  INVENTORY_LOCATIONS,
  STOCK_STATUS_LABEL,
  STOCK_STATUS_TONE,
  STOCK_VIEWS,
  availableOf,
  boqDemandByPart,
  findStockByCode,
  formatQty,
  itemDataIssue,
  onHandOf,
  paginate,
  plural,
  reservedOf,
  stockLocations,
  stockMatchesView,
  stockMovedParts,
  stockStatus,
  toQty
} from './inventoryModel';
import { downloadCsv, downloadXlsx } from './exportRows';
import StockItemDetail from './StockItemDetail';
import StockItemSheet from './StockItemSheet';
import { DeliveryPlansCallout, DeliveryPlansPhoneRow } from './DeliveryPlans';
import { labelEntries, printShelfLabels } from './labels';
import { QbQty, QbQtyLine, QbReadLine, qbItemOf, qbStockCompare } from './QuickBooksStock';

/** Prints shelf labels for these stock rows; says which part numbers cannot be a barcode. */
export function printLabelsFor(items) {
  const { entries, skipped } = labelEntries(items);
  if (!entries.length) {
    notify.error('No label to print', { description: `${skipped.join(', ') || 'These items'} — fix the part number first (Catalog).` });
    return;
  }
  if (!printShelfLabels(entries)) {
    notify.error('Allow pop-ups for this site to print the labels');
    return;
  }
  if (skipped.length) notify.warning(`${plural(skipped.length, 'item')} left out — not a valid part number`, { description: skipped.join(', ') });
}

const VIEW_LABEL = Object.fromEntries(STOCK_VIEWS.map((view) => [view.key, view.label]));

export const stockExportColumns = (siteDemandOf) => [
  { header: 'Part number', value: (row) => row.part_number },
  { header: 'Description', value: (row) => row.description || '' },
  { header: 'Brand', value: (row) => row.brand || '' },
  { header: 'Category', value: (row) => row.category || '' },
  { header: 'Status', value: (row) => STOCK_STATUS_LABEL[row.__status] || '' },
  ...INVENTORY_LOCATIONS.map((location) => ({ header: location, numeric: true, value: (row) => toQty(row.location_quantities?.[location]) })),
  { header: 'On hand', numeric: true, value: (row) => onHandOf(row) },
  { header: 'Reserved', numeric: true, value: (row) => reservedOf(row) },
  { header: 'Available', numeric: true, value: (row) => availableOf(row) },
  { header: 'Min level', numeric: true, value: (row) => toQty(row.min_stock_level) || '' },
  { header: 'Site demand', numeric: true, value: (row) => siteDemandOf(row) }
];

function countBy(rows, getValue) {
  const counts = new Map();
  rows.forEach((row) => {
    const value = String(getValue(row) || '').trim() || EMPTY_FILTER_VALUE;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return counts;
}

function facetOptions(counts, emptyLabel) {
  return [...counts.entries()]
    .sort((left, right) => (left[0] === EMPTY_FILTER_VALUE) - (right[0] === EMPTY_FILTER_VALUE) || left[0].localeCompare(right[0]))
    .map(([value, count]) => ({ value, count, label: value === EMPTY_FILTER_VALUE ? emptyLabel : value, muted: value === EMPTY_FILTER_VALUE }));
}

const matchesFacet = (value, filter) => {
  if (!filter) return true;
  const clean = String(value || '').trim();
  return filter === EMPTY_FILTER_VALUE ? !clean : clean === filter;
};

export default function StockTab({
  stockItems = [],
  movements = [],
  projects = [],
  sitesBoq = [],
  siteDemandOf: siteDemandProp,
  search = '',
  onSearchChange,
  brandFilter = null,
  onBrandFilterChange,
  categoryFilter = null,
  onCategoryFilterChange,
  quickFilter = 'has',
  onQuickFilterChange,
  locationFilter = null,
  onLocationFilterChange,
  projectFilter = null,
  onProjectFilterChange,
  needsFix = false,
  onNeedsFixChange,
  // QuickBooks Desktop, read only (QuickBooksStock.js): null = off / not read / failed — no column, no chip.
  qb = null,
  qbDiffers = false,
  onQbDiffersChange,
  selectedStockPartNumbers = [],
  toggleStockSelection,
  handleToggleAllStockSelection,
  onClearSelection,
  editingPartNumber = null,
  stockEditDraft = null,
  createStockEditDraft,
  handleStockDraftChange,
  handleCancelStockInlineEdit,
  handleSaveStockInlineEdit,
  onEditItem,
  onTransfer,
  onIssue,
  onReserve,
  onDelete,
  canDelete = false,
  reservations = [],
  onEditCatalog,
  onAdjust,
  canEditCatalog = true,
  canEdit = true,
  deliveryPlans = [],
  deliveryPlansLoading = false,
  deliveryPlansError = '',
  onOpenDeliveryPlans,
  onNewDeliveryPlan,
  expandedPart = null,
  onExpandedPartChange,
  onSetBin
}) {
  const isPhone = useMediaQuery('(max-width: 767px)');
  // Below xl the Reserved column is left out (Bookings are in the expanded row), so rows span one column less.
  const columnCount = (useMediaQuery('(min-width: 1280px)') ? 9 : 8) + (qb ? 1 : 0);
  const [ownExpanded, setOwnExpanded] = useState(null);
  const [sheetPart, setSheetPart] = useState(null);
  // The last scan (18 #10): { code, partNumber, at, source } — the sheet shows "Scanned · … · just now".
  const [scanned, setScanned] = useState(null);
  const expanded = onExpandedPartChange ? expandedPart : ownExpanded;
  const setExpanded = onExpandedPartChange || setOwnExpanded;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Only movements that changed stock count as history (a direct-to-site DN never did).
  const movedParts = useMemo(() => stockMovedParts(movements), [movements]);
  const isEditing = Boolean(editingPartNumber);
  const projectNames = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);

  /** Part numbers each project still needs (remaining BOQ). */
  const projectDemand = useMemo(() => {
    const byProject = new Map();
    sitesBoq.forEach((row) => {
      (row.project_details || []).forEach((detail) => {
        if (toQty(detail.remaining_qty) <= 0) return;
        if (!byProject.has(detail.project_id)) byProject.set(detail.project_id, { name: detail.project_name, parts: new Map() });
        byProject.get(detail.project_id).parts.set(row.part_number, toQty(detail.remaining_qty));
      });
    });
    return byProject;
  }, [sitesBoq]);

  // Site demand = the Sites BOQ still to deliver that nobody has booked (the page passes the same
  // function to the Dashboard's reorder list, so both show one number).
  const ownDemand = useMemo(() => boqDemandByPart(sitesBoq), [sitesBoq]);
  const siteDemandOf = siteDemandProp || ((item) => toQty(ownDemand.get(item.part_number)));

  /** Per part: the projects whose BOQ still needs it (for the expanded row). */
  const demandByPart = useMemo(() => new Map(sitesBoq.map((row) => [
    row.part_number,
    (row.project_details || []).filter((detail) => toQty(detail.remaining_qty) > 0)
  ])), [sitesBoq]);
  const reservationsByPart = useMemo(() => {
    const map = new Map();
    reservations.forEach((reservation) => {
      if (!map.has(reservation.part_number)) map.set(reservation.part_number, []);
      map.get(reservation.part_number).push(reservation);
    });
    return map;
  }, [reservations]);
  const movementsByPart = useMemo(() => {
    const map = new Map();
    movements.forEach((movement) => {
      if (!map.has(movement.part_number)) map.set(movement.part_number, []);
      map.get(movement.part_number).push(movement);
    });
    return map;
  }, [movements]);

  // Part number → QuickBooks item, once per stock list (not per row render).
  const qbByPart = useMemo(() => new Map(qb ? stockItems.map((item) => [item.part_number, qbItemOf(qb, item.part_number)]) : []), [qb, stockItems]);
  const qbDifferCount = useMemo(
    () => stockItems.filter((item) => qbStockCompare(qbByPart.get(item.part_number), onHandOf(item)).differs).length,
    [qbByPart, stockItems]
  );
  const qbDiffersOn = Boolean(qb) && qbDiffers;

  // Search + chips (everything except the view) — the segmented counts follow these.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const demandParts = projectFilter ? projectDemand.get(projectFilter)?.parts : null;
    return stockItems.filter((item) => {
      if (query && ![item.part_number, item.description].filter(Boolean).join(' ').toLowerCase().includes(query)) return false;
      if (!matchesFacet(item.brand, brandFilter)) return false;
      if (!matchesFacet(item.category, categoryFilter)) return false;
      if (locationFilter && toQty(item.location_quantities?.[locationFilter]) <= 0) return false;
      if (projectFilter && !demandParts?.has(item.part_number)) return false;
      if (needsFix && !itemDataIssue(item)) return false;
      if (qbDiffersOn && !qbStockCompare(qbByPart.get(item.part_number), onHandOf(item)).differs) return false;
      return true;
    });
  }, [brandFilter, categoryFilter, locationFilter, needsFix, projectDemand, projectFilter, qbByPart, qbDiffersOn, search, stockItems]);

  const viewCounts = useMemo(() => Object.fromEntries(
    STOCK_VIEWS.map((view) => [view.key, filtered.filter((item) => stockMatchesView(item, view.key, movedParts)).length])
  ), [filtered, movedParts]);

  const visibleStock = useMemo(() => filtered
    .filter((item) => stockMatchesView(item, quickFilter, movedParts))
    .map((item) => ({ ...item, __status: stockStatus(item, movedParts) }))
    .sort((left, right) => (
      availableOf(right) - availableOf(left)
      || onHandOf(right) - onHandOf(left)
      || String(left.part_number).localeCompare(String(right.part_number))
    )), [filtered, movedParts, quickFilter]);

  useEffect(() => { setPage(1); }, [search, brandFilter, categoryFilter, locationFilter, projectFilter, needsFix, qbDiffersOn, quickFilter, pageSize]);

  const paged = paginate(visibleStock, page, pageSize);
  const pageRows = paged.rows;
  // The editor lives in its row: when that row leaves the page (search, view, paging), close it.
  const editingOnPage = !isEditing || pageRows.some((item) => item.part_number === editingPartNumber);
  useEffect(() => {
    if (!editingOnPage) handleCancelStockInlineEdit?.();
  }, [editingOnPage, handleCancelStockInlineEdit]);
  const selectedSet = new Set(selectedStockPartNumbers);
  const pageSelectedCount = pageRows.filter((item) => selectedSet.has(item.part_number)).length;
  const allPageSelected = pageRows.length > 0 && pageSelectedCount === pageRows.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const dataIssueRows = useMemo(() => stockItems.filter((item) => itemDataIssue(item) && onHandOf(item) > 0), [stockItems]);
  const dataIssuePcs = dataIssueRows.reduce((sum, item) => sum + onHandOf(item), 0);

  const brandOptions = useMemo(() => facetOptions(countBy(stockItems, (item) => item.brand), 'No brand'), [stockItems]);
  const categoryOptions = useMemo(() => facetOptions(countBy(stockItems, (item) => item.category), 'No category'), [stockItems]);
  const locationOptions = useMemo(() => INVENTORY_LOCATIONS.map((location) => ({
    value: location,
    label: location,
    count: stockItems.filter((item) => toQty(item.location_quantities?.[location]) > 0).length
  })), [stockItems]);
  const projectOptions = useMemo(() => [...projectDemand.entries()]
    .map(([id, entry]) => ({ value: id, label: projectNames.get(id) || entry.name || id, count: entry.parts.size }))
    .sort((left, right) => left.label.localeCompare(right.label)), [projectDemand, projectNames]);

  const exportRows = (rows, label) => {
    const columns = stockExportColumns(siteDemandOf);
    return { filename: `stock-${label}`, sheetName: 'Stock', columns, rows };
  };
  const selectedRows = stockItems
    .filter((item) => selectedSet.has(item.part_number))
    .map((item) => ({ ...item, __status: stockStatus(item, movedParts) }));


  const clearAllFilters = () => {
    onSearchChange?.('');
    onBrandFilterChange?.(null);
    onCategoryFilterChange?.(null);
    onLocationFilterChange?.(null);
    onProjectFilterChange?.(null);
    onNeedsFixChange?.(false);
    onQbDiffersChange?.(false);
  };
  const hasFilters = Boolean(search || brandFilter || categoryFilter || locationFilter || projectFilter || needsFix || qbDiffersOn);

  /**
   * Scan & move (18 #10): a shelf label or box barcode opens that item — its sheet on a phone, its row on a
   * wider screen — with every filter that could hide it cleared. An unknown code stays in the search box.
   */
  const handleScan = (code, { source } = {}) => {
    const text = String(code ?? '').trim();
    if (!text) return;
    const item = findStockByCode(stockItems, text);
    if (!item) {
      onSearchChange?.(text);
      notify.warning(`No stock item with part number ${text}`, { description: 'The search shows anything close to it.' });
      return;
    }
    onBrandFilterChange?.(null);
    onCategoryFilterChange?.(null);
    onLocationFilterChange?.(null);
    onProjectFilterChange?.(null);
    onNeedsFixChange?.(false);
    onQbDiffersChange?.(false);
    if (!stockMatchesView(item, quickFilter, movedParts)) onQuickFilterChange?.('all');
    onSearchChange?.(item.part_number);
    setScanned({ code: text, partNumber: item.part_number, at: Date.now(), source: source || 'camera' });
    if (isPhone) setSheetPart(item.part_number);
    else setExpanded(item.part_number);
  };

  const segOptions = STOCK_VIEWS
    .filter((view) => !view.optional || viewCounts[view.key] > 0 || quickFilter === view.key)
    .map((view) => ({
      key: view.key,
      label: view.label,
      count: viewCounts[view.key],
      testId: `stock-filter-${view.key}`
    }));

  // "Differs from QuickBooks" — a toggle chip next to Needs fixing. Turning it on shows All: a line with nothing
  // here but stock in QuickBooks differs too, and the default "In stock" view would hide it.
  const qbChip = qb ? (
    <FilterChip
      label={qbDiffersOn ? 'Showing' : 'Differs from QuickBooks'}
      value={qbDiffersOn ? 'Differs from QuickBooks' : null}
      icon={qbDiffersOn ? TriangleAlert : undefined}
      onClick={() => {
        if (qbDiffersOn) { onQbDiffersChange?.(false); return; }
        onQbDiffersChange?.(true);
        onQuickFilterChange?.('all');
      }}
      onClear={() => onQbDiffersChange?.(false)}
      title={`${plural(qbDifferCount, 'stock line')} with another quantity on hand in QuickBooks`}
      data-testid="stock-chip-qb-differs"
    />
  ) : null;

  const footLeft = (
    <span>
      {visibleStock.length ? `${formatNumber(paged.start + 1)}–${formatNumber(paged.end)} of ` : '0 of '}
      <b className="font-semibold text-ink-2">{formatNumber(visibleStock.length)}</b> in <b className="font-semibold text-ink-2">{VIEW_LABEL[quickFilter] || 'All'}</b>
      {' · '}{formatNumber(stockItems.length)} total
    </span>
  );

  return (
    <div className="space-y-4" data-testid="inventory-stock-tab">
      {/* Toolbar: search · chips · view · export */}
      <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
        {/* Search or scan (18 #10): the barcode button opens the camera scanner (typing the code always works). */}
        <div className="flex h-11 w-full min-w-0 shrink-0 items-stretch overflow-hidden rounded-control border border-line bg-surface focus-within:border-[color:var(--brand-ink)] focus-within:ring-2 focus-within:ring-[color:var(--brand-soft-border)] sm:h-9 sm:w-[214px] xl:w-[232px]" data-testid="stock-search-scan">
          <SearchField
            value={search}
            onChange={onSearchChange}
            placeholder={isPhone ? 'Search or scan part no.' : 'Search part no.…'}
            className="h-full flex-1 rounded-none border-0 focus-within:border-0 focus-within:ring-0"
            label="Search stock"
          />
          <ScanButton
            onDetected={handleScan}
            label="Scan a part number"
            className="h-full w-11 rounded-none border-l border-line sm:w-9 [&_svg]:sm:h-4 [&_svg]:sm:w-4"
            testId="stock-scan-button"
            scannerProps={{
              title: 'Scan a part number',
              description: 'Point the camera at the shelf label or the barcode on the box.',
              manualLabel: 'Or type the part number',
              testId: 'stock-scanner'
            }}
          />
        </div>
        {isPhone ? (
          // Phones (mobile spec): ONE row that scrolls sideways — the views as 44px chips, then the filters.
          <ScrollFade className="w-full" testId="stock-phone-filters">
            <div className="flex w-max items-center gap-2 pr-2 [&_[data-state-set]]:h-11 [&_[data-state-set]]:text-[13px]" role="group" aria-label="Stock view and filters">
              {segOptions.map((option) => {
                const active = option.key === quickFilter;
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onQuickFilterChange?.(option.key)}
                    className={cn(
                      'inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active ? 'border-brand bg-[color:var(--navy-50)] text-brand-ink' : 'border-line bg-surface text-ink-2'
                    )}
                    data-testid={option.testId}
                  >
                    {option.label}
                    <span className={cn('text-[12px] tabular-nums', active ? 'text-brand-ink' : 'text-ink-3')}>{formatNumber(option.count ?? 0)}</span>
                  </button>
                );
              })}
              <span className="mx-0.5 h-6 w-px shrink-0 bg-line" aria-hidden="true" />
              <ChipSelect label="Location" icon={locationFilter ? MapPin : undefined} value={locationFilter} options={locationOptions} onChange={onLocationFilterChange} testId="stock-chip-location" />
              <ChipSelect label="Brand" value={brandFilter} options={brandOptions} onChange={onBrandFilterChange} testId="stock-chip-brand" />
              <ChipSelect label="Category" value={categoryFilter} options={categoryOptions} onChange={onCategoryFilterChange} testId="stock-chip-category" />
              <ChipSelect label="Project" value={projectFilter} options={projectOptions} onChange={onProjectFilterChange} emptyText="No project is waiting for items" testId="stock-chip-project" />
              {needsFix ? (
                <FilterChip label="Showing" value="Needs fixing" icon={TriangleAlert} onClear={() => onNeedsFixChange?.(false)} />
              ) : null}
              {qbChip}
            </div>
          </ScrollFade>
        ) : (
          <>
            <ChipSelect label="Location" icon={locationFilter ? MapPin : undefined} value={locationFilter} options={locationOptions} onChange={onLocationFilterChange} testId="stock-chip-location" />
            <ChipSelect label="Brand" value={brandFilter} options={brandOptions} onChange={onBrandFilterChange} testId="stock-chip-brand" />
            <ChipSelect label="Category" value={categoryFilter} options={categoryOptions} onChange={onCategoryFilterChange} testId="stock-chip-category" />
            <ChipSelect label="Project" value={projectFilter} options={projectOptions} onChange={onProjectFilterChange} emptyText="No project is waiting for items" testId="stock-chip-project" />
            {needsFix ? (
              <FilterChip label="Showing" value="Needs fixing" icon={TriangleAlert} onClear={() => onNeedsFixChange?.(false)} />
            ) : null}
            {qbChip}
            <span className="hidden flex-1 xl:block" />
            {/* One toolbar line on a laptop: when the chips take the room, the views scroll inside (with a fade). */}
            <ScrollFade className="xl:shrink" testId="stock-view-scroll">
              <Segmented className="max-w-none" options={segOptions} value={quickFilter} onChange={onQuickFilterChange} ariaLabel="Stock view" />
            </ScrollFade>
            <ExportMenu
              iconOnly
              label="Export this view"
              testId="stock-export-btn"
              disabled={!visibleStock.length}
              onExcel={() => downloadXlsx(exportRows(visibleStock, quickFilter))}
              onCsv={() => downloadCsv(exportRows(visibleStock, quickFilter))}
            />
          </>
        )}
      </div>

      {/* Slim callouts: saved delivery plans + data health */}
      {isPhone ? <DeliveryPlansPhoneRow plans={deliveryPlans} onOpenList={onOpenDeliveryPlans} /> : null}
      <div className={cn('grid gap-4', dataIssueRows.length ? 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]' : '')}>
        <DeliveryPlansCallout
          className="hidden md:flex"
          plans={deliveryPlans}
          loading={deliveryPlansLoading}
          error={deliveryPlansError}
          onOpenList={onOpenDeliveryPlans}
          onNew={canEdit ? onNewDeliveryPlan : undefined}
        />
        {dataIssueRows.length ? (
          <Callout
            tone="amber"
            icon={TriangleAlert}
            data-testid="stock-data-health"
            subWrap
            title={(
              <>
                <b className="font-bold">{plural(dataIssueRows.length, 'stock line')} need{dataIssueRows.length === 1 ? 's' : ''} fixing</b>
                <span className="text-status-amber-ink"> · {formatQty(dataIssuePcs)} pcs on these records</span>
              </>
            )}
            sub={(
              <>No part no. / description: <span className="font-mono text-[11.5px] text-ink">{dataIssueRows.map((item) => item.part_number || '(blank)').join(', ')}</span></>
            )}
            action={(
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-ink max-md:h-11"
                onClick={() => { onNeedsFixChange?.(true); onQuickFilterChange?.('all'); }}
                data-testid="stock-show-needs-fix"
              >
                Show &amp; fix
              </Button>
            )}
          />
        ) : null}
      </div>

      {qb ? <QbReadLine readAt={qb.readAt} differCount={qbDifferCount} className="-mb-2" /> : null}

      {/* Phone: cards (part · status · where · available) */}
      {isPhone ? (
      <div className="space-y-2" data-testid="stock-cards">
        {pageRows.map((item) => {
          const issue = itemDataIssue(item);
          return (
            <article
              key={item.part_number}
              role="button"
              tabIndex={0}
              onClick={() => setSheetPart(item.part_number)}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSheetPart(item.part_number); } }}
              aria-label={`Open ${item.part_number || 'item'}`}
              className={cn('flex cursor-pointer items-center gap-3 rounded-xl border bg-surface px-4 py-3 shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', sheetPart === item.part_number ? 'border-brand' : 'border-line')}
              data-testid={`stock-card-${item.part_number}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-[13px] text-ink">{item.part_number || '—'}</p>
                {issue ? (
                  <p className="mt-0.5 flex items-center gap-1 text-[12px] text-status-amber-ink"><TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />{issue}</p>
                ) : (
                  <p className="mt-0.5 truncate text-[12.5px] text-ink-2">{item.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <StatusPill tone={STOCK_STATUS_TONE[item.__status]} label={STOCK_STATUS_LABEL[item.__status]} size="sm" />
                  {stockLocations(item).slice(0, 2).map((entry) => <LocationTag key={entry.location} location={entry.location} />)}
                </div>
                {/* No room for a QuickBooks column on a phone: its quantity is a line of the card. */}
                {qb ? <QbQtyLine qbItem={qbByPart.get(item.part_number)} here={onHandOf(item)} testId={`stock-card-qb-${item.part_number}`} /> : null}
              </div>
              <div className="shrink-0 text-right">
                <p className="font-heading text-[24px] font-black leading-none tabular-nums text-ink">{formatQty(availableOf(item))}</p>
                <p className="mt-1 text-[11.5px] text-ink-3">available</p>
              </div>
            </article>
          );
        })}
        {!pageRows.length ? (
          <EmptyState icon={SearchX} title="No stock items found" text="Try another view or clear the search." />
        ) : null}
        <TableFoot className="rounded-xl border border-line px-2.5 [&_nav]:flex-wrap [&_nav]:justify-end [&_nav]:gap-1 [&_nav_button]:h-11 [&_nav_button]:min-w-10 [&_nav_button]:rounded-lg [&_nav_button]:text-[13px]" left={footLeft} page={paged.page} pageCount={paged.pageCount} onPage={setPage} />
        <StockItemSheet
          item={sheetPart ? pageRows.find((row) => row.part_number === sheetPart) || stockItems.find((row) => row.part_number === sheetPart) : null}
          status={sheetPart ? stockStatus(stockItems.find((row) => row.part_number === sheetPart) || {}, movedParts) : null}
          demand={sheetPart ? demandByPart.get(sheetPart) || [] : []}
          scanned={scanned && scanned.partNumber === sheetPart ? scanned : null}
          onOpenChange={(open) => { if (!open) { setSheetPart(null); setScanned(null); } }}
          onIssue={() => onIssue?.([sheetPart])}
          onTransfer={() => onTransfer?.([sheetPart])}
          onReserve={() => onReserve?.([sheetPart])}
          onAdjust={onAdjust}
          canEdit={canEdit}
        />
      </div>
      ) : (
      /* Desktop / tablet: 9-column table (10 with the QuickBooks column) */
      <TableWrap data-testid="stock-table">
        <TableScroll>
          <table className={cn(tableClass, 'table-fixed', qb ? 'min-w-[932px] xl:min-w-[1052px]' : 'min-w-[840px] xl:min-w-[960px]')}>
            <colgroup>
              <col className="w-11" />
              <col />
              <col className="w-[112px]" />
              <col className="w-[140px]" />
              <col className="w-[84px]" />
              {qb ? <col className="w-[92px]" /> : null}
              <col className="hidden w-[84px] xl:table-column" />
              <col className="w-[92px]" />
              <col className="w-[110px]" />
              <col className="w-[68px]" />
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}>
                  <InvCheckbox
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    disabled={isEditing}
                    onCheckedChange={() => handleToggleAllStockSelection(!allPageSelected, pageRows)}
                    aria-label="Select all rows on this page"
                    title={isEditing ? 'Save or cancel the open edit first' : undefined}
                  />
                </th>
                <th className={thClass}>Item</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Where</th>
                <th className={cn(thClass, numClass)}>On hand</th>
                {qb ? <th className={cn(thClass, numClass)} title="Quantity on hand in QuickBooks Desktop (read only), compared with On hand" data-testid="stock-qb-col">QuickBooks</th> : null}
                <th className={cn(thClass, numClass, 'hidden xl:table-cell')}>Reserved</th>
                <th className={cn(thClass, numClass, 'text-ink')}>Available ↓</th>
                <th className={cn(thClass, numClass)} title="Sites BOQ still to deliver, minus what is already booked">Site demand</th>
                <th className={thClass}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((item) => {
                const partNumber = item.part_number;
                const selected = selectedSet.has(partNumber);
                const isEditingRow = isEditing && editingPartNumber === partNumber;
                const draft = (isEditingRow && stockEditDraft) || createStockEditDraft(item);
                const issue = itemDataIssue(item);
                const available = availableOf(item);
                const onHand = onHandOf(item);
                const reserved = reservedOf(item);
                const demand = siteDemandOf(item);
                const short = Math.max(0, demand - available);
                const where = stockLocations(item);
                const meta = [item.description, item.brand, item.category].filter((value) => String(value || '').trim()).join(' · ');
                const open = expanded === partNumber && !isEditingRow;
                const toggleOpen = () => setExpanded(open ? null : partNumber);
                return (
                  <Fragment key={partNumber}>
                    <tr
                      className={cn('group cursor-pointer', selected || isEditingRow || open ? '[&>td]:bg-brand-soft' : '[&:hover>td]:bg-surface-hover', open && '[&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]')}
                      onClick={(event) => {
                        if (isEditing || event.target.closest('button, a, input, [role="checkbox"], [role="menu"], [data-row-click="ignore"]')) return;
                        toggleOpen();
                      }}
                      aria-expanded={open}
                      data-testid={`stock-row-${partNumber}`}
                    >
                      <td className={tdClass} data-row-click="ignore" onClick={(event) => event.stopPropagation()}>
                        <InvCheckbox
                          checked={selected}
                          disabled={isEditing}
                          onCheckedChange={(checked) => toggleStockSelection(partNumber, Boolean(checked))}
                          aria-label={`Select ${partNumber || 'row'}`}
                          title={isEditing ? 'Save or cancel the open edit first' : undefined}
                        />
                      </td>
                      <td className={cn(tdClass, 'py-1.5')}>
                        <span className="block truncate font-mono text-[12.5px] text-ink" title={partNumber}>{partNumber || '—'}</span>
                        {issue ? (
                          <span className="mt-px flex items-center gap-1 truncate text-[11.5px] text-status-amber-ink">
                            <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />{issue}
                            {item.description && issue !== 'No description' ? <span className="truncate text-ink-3"> · {item.description}</span> : null}
                          </span>
                        ) : (
                          <span className={cn(subClass, 'truncate')} title={meta}>{meta}</span>
                        )}
                      </td>
                      <td className={tdClass}>
                        <StatusPill tone={STOCK_STATUS_TONE[item.__status]} label={STOCK_STATUS_LABEL[item.__status]} data-testid={`stock-status-${partNumber}`} />
                      </td>
                      <td className={tdClass}>
                        <span className="flex min-w-0 flex-wrap gap-1">
                          {where.length ? where.slice(0, 2).map((entry) => (
                            <LocationTag key={entry.location} location={entry.location} qty={entry.qty} />
                          )) : <span className="text-ink-4" aria-label="Nowhere">—</span>}
                          {where.length > 2 ? <span className="self-center text-[11.5px] text-ink-3">+{where.length - 2}</span> : null}
                        </span>
                      </td>
                      <td className={cn(tdClass, numClass, 'text-ink-2')}>{formatQty(onHandOf(item))}</td>
                      {qb ? (
                        <td className={cn(tdClass, numClass)}>
                          <QbQty qbItem={qbByPart.get(partNumber)} here={onHand} testId={`stock-qb-${partNumber}`} />
                        </td>
                      ) : null}
                      <td className={cn(tdClass, numClass, 'hidden xl:table-cell', reserved > 0 ? 'font-semibold text-status-blue-ink' : 'text-ink-3')}>{formatQty(reserved)}</td>
                      <td className={cn(tdClass, numClass, 'font-bold', item.__status === 'low' ? 'text-status-amber-ink' : available > 0 ? 'text-ink' : 'text-ink-3')} data-testid={`stock-available-${partNumber}`}>
                        {formatQty(available)}
                      </td>
                      <td
                        className={cn(tdClass, numClass)}
                        title={toQty(item.lpo_remaining) > 0 ? `Incoming on LPO: ${formatQty(item.lpo_remaining)} pcs still to arrive` : undefined}
                      >
                        <span className={cn(short > 0 ? 'font-bold text-status-red-ink' : demand > 0 ? 'text-ink' : 'text-ink-3')}>{formatQty(demand)}</span>
                        {short > 0 ? <span className="block text-[11.5px] text-status-red-ink">Short {formatQty(short)}</span> : null}
                      </td>
                      <td className={cn(tdClass, 'pl-0 pr-2 text-right')}>
                        <span className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); toggleOpen(); }}
                            disabled={isEditing}
                            aria-label={`${open ? 'Hide' : 'Show'} details of ${partNumber || 'item'}`}
                            aria-expanded={open}
                            className="grid h-7 w-7 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                            data-testid={`stock-expand-${partNumber}`}
                          >
                            {open ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                          </button>
                          <RowActions
                            label={`Actions for ${partNumber || 'item'}`}
                            className="opacity-60 group-hover:opacity-100 focus-visible:opacity-100"
                            items={canEdit ? [
                              { label: 'Transfer…', icon: ArrowRightLeft, onSelect: () => onTransfer?.([partNumber]), disabled: available <= 0 },
                              // Booked stock can still go to the project that booked it.
                              { label: 'Issue to site…', icon: Truck, onSelect: () => onIssue?.([partNumber]), disabled: onHand <= 0 },
                              { label: 'Reserve for a project…', icon: Lock, onSelect: () => onReserve?.([partNumber]), disabled: available <= 0 },
                              { label: 'Adjust stock…', icon: Scale, onSelect: () => setExpanded(partNumber), testId: `stock-menu-adjust-${partNumber}` },
                              { label: 'Print shelf label', icon: Printer, onSelect: () => printLabelsFor([item]), testId: `stock-menu-label-${partNumber}` },
                              item.catalog_id && !issue
                                ? { label: canEditCatalog ? 'Edit in Catalog…' : 'Open in Catalog', icon: Pencil, onSelect: () => onEditCatalog?.(item), testId: `stock-edit-${partNumber}` }
                                : { label: 'Fix part no. / description…', icon: Pencil, onSelect: () => onEditItem?.(partNumber), testId: `stock-edit-${partNumber}` },
                              canDelete && item.catalog_id ? { label: 'Delete item…', icon: Trash2, danger: true, onSelect: () => onDelete?.([partNumber]) } : null
                            ] : [
                              item.catalog_id ? { label: 'Open in Catalog', icon: Pencil, onSelect: () => onEditCatalog?.(item), testId: `stock-edit-${partNumber}` } : null
                            ]}
                          />
                        </span>
                      </td>
                    </tr>
                    {open ? (
                      <tr data-testid={`stock-expansion-${partNumber}`}>
                        <td colSpan={columnCount} className="border-b border-line-2 bg-surface-2 px-5 pb-4 pt-3.5 shadow-[inset_3px_0_0_var(--brand)]">
                          <StockItemDetail
                            item={item}
                            reservations={reservationsByPart.get(partNumber) || []}
                            demand={demandByPart.get(partNumber) || []}
                            movements={movementsByPart.get(partNumber) || []}
                            canEditCatalog={canEditCatalog}
                            canEdit={canEdit}
                            onIssue={() => onIssue?.([partNumber])}
                            onTransfer={() => onTransfer?.([partNumber])}
                            onReserve={() => onReserve?.([partNumber])}
                            onEditCatalog={() => onEditCatalog?.(item)}
                            onFixItem={() => onEditItem?.(partNumber)}
                            onAdjust={onAdjust}
                            onPrintLabel={() => printLabelsFor([item])}
                            onSetBin={onSetBin}
                            scanned={scanned && scanned.partNumber === partNumber ? scanned : null}
                            qbItem={qbByPart.get(partNumber) || null}
                            qbReadAt={qb?.readAt}
                          />
                        </td>
                      </tr>
                    ) : null}
                    {isEditingRow ? (
                      <tr data-testid="stock-edit-row">
                        <td
                          colSpan={columnCount}
                          className="border-b border-line-2 bg-surface-2 px-4 py-3"
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') { event.preventDefault(); handleCancelStockInlineEdit?.(); }
                            if (event.key === 'Enter' && event.target.tagName === 'INPUT') { event.preventDefault(); handleSaveStockInlineEdit?.(); }
                          }}
                        >
                          <div className="grid gap-3 pl-8 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_auto] xl:items-end">
                            <EditField label="Part number" value={draft.part_number} onChange={(value) => handleStockDraftChange(partNumber, 'part_number', value)} mono testId="stock-edit-part-number" autoFocus />
                            <EditField label="Description" value={draft.description} onChange={(value) => handleStockDraftChange(partNumber, 'description', value)} testId="stock-edit-description" />
                            <EditField label="Brand" value={draft.brand} onChange={(value) => handleStockDraftChange(partNumber, 'brand', value)} testId="stock-edit-brand" />
                            <EditField label="Category" value={draft.category} onChange={(value) => handleStockDraftChange(partNumber, 'category', value)} testId="stock-edit-category" />
                            <div className="flex items-center gap-2">
                              <Button type="button" size="sm" variant="ghost" onClick={handleCancelStockInlineEdit} data-testid="stock-edit-cancel">Cancel</Button>
                              <Button type="button" size="sm" onClick={handleSaveStockInlineEdit} data-testid="stock-edit-save">Save</Button>
                            </div>
                          </div>
                          <p className="mt-2 pl-8 text-[11.5px] text-ink-3">Saves this item only · quantities change only through movements (GRN, transfer, adjustment…) · Enter saves, Esc cancels.</p>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        {!pageRows.length ? (
          <EmptyState
            layout="stacked"
            icon={hasFilters ? SearchX : PackageSearch}
            title={hasFilters ? 'No stock items found' : `No items in “${VIEW_LABEL[quickFilter]}”`}
            text={hasFilters ? 'Nothing matches these filters in this view.' : 'Try another view — the counts above show where items are.'}
            action={hasFilters ? <Button size="sm" variant="outline" className="text-ink" onClick={clearAllFilters}>Clear filters</Button> : null}
          />
        ) : null}
        <TableFoot
          left={footLeft}
          page={paged.page}
          pageCount={paged.pageCount}
          onPage={setPage}
          pageSize={pageSize}
          onPageSize={setPageSize}
        />
      </TableWrap>
      )}

      {/* Hidden while a row is being edited — one task at a time. */}
      <BulkBar count={isEditing ? 0 : selectedStockPartNumbers.length} onClear={onClearSelection}>
        {canEdit ? <BulkButton icon={ArrowRightLeft} onClick={() => onTransfer?.(selectedStockPartNumbers)}>Transfer</BulkButton> : null}
        {canEdit ? <BulkButton icon={Truck} onClick={() => onIssue?.(selectedStockPartNumbers)}>Issue to site</BulkButton> : null}
        {!canEdit ? null : selectedStockPartNumbers.length > 1 ? (
          // A booking is for one item; the dialog would only carry the first one.
          <span title="Reserve one item at a time" className="inline-flex" data-testid="stock-bulk-reserve-hint">
            <BulkButton icon={Lock} disabled aria-label="Reserve — one item at a time">Reserve</BulkButton>
          </span>
        ) : (
          <BulkButton icon={Lock} onClick={() => onReserve?.(selectedStockPartNumbers)}>Reserve</BulkButton>
        )}
        <BulkButton icon={Printer} onClick={() => printLabelsFor(selectedRows)} data-testid="stock-bulk-labels">Labels</BulkButton>
        <BulkButton icon={Download} onClick={() => downloadXlsx(exportRows(selectedRows, 'selection'))}>Export</BulkButton>
        {canDelete ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <BulkButton aria-label="More bulk actions" data-testid="stock-bulk-more"><Ellipsis className="h-4 w-4" aria-hidden="true" /></BulkButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="min-w-[220px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
              <DropdownMenuItem
                className="h-[34px] gap-2.5 rounded-lg text-[13px]"
                onSelect={() => setTimeout(() => downloadCsv(exportRows(selectedRows, 'selection')), 0)}
              >
                <Download className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> Export as CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-line-2" />
              <DropdownMenuItem
                className="h-auto gap-2.5 rounded-lg py-1.5 text-[13px] text-status-red-ink focus:bg-status-red-bg focus:text-status-red-ink"
                onSelect={() => setTimeout(() => onDelete?.(selectedStockPartNumbers), 0)}
                data-testid="stock-bulk-delete"
              >
                <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                <span>
                  <span className="block font-semibold">Delete {plural(selectedStockPartNumbers.length, 'item')}…</span>
                  <span className="block text-[11.5px] text-ink-3">Removes stock and history · type the count to confirm</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </BulkBar>
    </div>
  );
}

function EditField({ label, value, onChange, mono, testId, autoFocus }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11.5px] font-semibold text-ink-3">{label}</span>
      <Input
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className={cn('h-8 text-[12.5px]', mono && 'font-mono')}
        data-testid={testId}
        autoFocus={autoFocus}
      />
    </label>
  );
}
